import { useState, useEffect } from 'react';
import type { BreadcrumbItem } from './Breadcrumbs';
import { buildBreadcrumbSchema, type BreadcrumbSchema } from './utils';

const formatPathSegment = (segment: string) => {
  if (!segment) return '';
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface UseBreadcrumbsProps {
  pageTitle?: string;
}

/** The crumbs to render plus the JSON-LD schema that mirrors them. */
interface UseBreadcrumbsResult {
  crumbs: Array<BreadcrumbItem>;
  schema: BreadcrumbSchema;
}

/** Derives crumbs from `window.location` and `document.title`, so the first (SSR) render is always empty. */
const useBreadcrumbs = ({ pageTitle }: UseBreadcrumbsProps): UseBreadcrumbsResult => {
  const [generatedBreadcrumbs, setGeneratedBreadcrumbs] = useState<Array<BreadcrumbItem>>([]);
  const [schema, setSchema] = useState<BreadcrumbSchema>(() => buildBreadcrumbSchema([]));

  useEffect(() => {
    // Detects the browser at runtime; there is no value to parse at a boundary here.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof window !== 'undefined') {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);

      // Create home breadcrumb
      const homeBreadcrumb = { label: 'Home', href: '/' };

      const pathBreadcrumbs = pathSegments.map((segment, index) => {
        const isLast = index === pathSegments.length - 1;

        let href: string | undefined;
        if (!isLast) {
          href = `/${pathSegments.slice(0, index + 1).join('/')}`;
        }

        return {
          label: formatPathSegment(segment),
          href,
        };
      });

      // eslint-disable-next-line react-hooks/set-state-in-effect -- requires window.location which is only available in browser
      setGeneratedBreadcrumbs([homeBreadcrumb, ...pathBreadcrumbs]);
    }
  }, []);

  useEffect(() => {
    if (generatedBreadcrumbs.length > 0) {
      const lastItem = generatedBreadcrumbs[generatedBreadcrumbs.length - 1];
      // Detects the browser at runtime; there is no value to parse at a boundary here.
      // oxlint-disable-next-line anti-slop/no-runtime-typeof
      const title = pageTitle || (typeof document !== 'undefined' ? document.title.replace('| Chime', '').trim() : '');
      if (title && lastItem && lastItem.label !== title) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- derives breadcrumb label from document.title
        setGeneratedBreadcrumbs(prev => {
          const currentLastItem = prev[prev.length - 1];
          return [...prev.slice(0, -1), { ...currentLastItem, label: title }];
        });
      }

      setSchema(buildBreadcrumbSchema(generatedBreadcrumbs));
    }
  }, [generatedBreadcrumbs, pageTitle]);

  return { crumbs: generatedBreadcrumbs, schema };
};

export default useBreadcrumbs;
